import { readCompatibilityPairs } from './compatibility-bundle-fields';
import {
  COMPATIBILITY_MATCH_TAG,
  KEPT_PAIRING_TAG,
  LEGACY_KEPT_PAIRING_TAG,
  readExistingSystemUniqueId
} from './compatibility-systems';

export type DuctedCompareCardLookup = {
  cardId: string;
  systemUniqueId: string;
  keptPairing?: boolean;
  combination: Record<string, unknown>;
};

export type DuctedComparedSystem = {
  id: string;
  tags?: string[];
  existingSystemUniqueId?: string;
  compatibilityCombinationId?: number;
  ductedCompareCardId?: string;
  configuration?: {
    condensers?: Array<{
      selectedProduct?: { model?: string | null } | null;
      zones?: Array<{
        selectedProduct?: { model?: string | null } | null;
        blowerProduct?: { model?: string | null } | null;
      }>;
    }>;
  };
};

export type SlotModels = {
  outdoor: string;
  indoor: string;
  blower: string;
};

export const normalizeModel = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

export const asCombinationId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

export const withDuctedCompareIdentity = <
  T extends Pick<
    DuctedComparedSystem,
    'ductedCompareCardId' | 'compatibilityCombinationId'
  >
>(
  system: T,
  lookup: DuctedCompareCardLookup
): T => {
  const combinationId = asCombinationId(lookup.combination.ID);
  return {
    ...system,
    ductedCompareCardId: lookup.cardId,
    ...(combinationId != null
      ? { compatibilityCombinationId: combinationId }
      : {})
  };
};

const isCompatibilitySystem = (system: DuctedComparedSystem): boolean =>
  (system.tags ?? []).includes(COMPATIBILITY_MATCH_TAG);

const hasKeptPairing = (system: DuctedComparedSystem): boolean => {
  const tags = system.tags ?? [];
  return (
    tags.includes(KEPT_PAIRING_TAG) || tags.includes(LEGACY_KEPT_PAIRING_TAG)
  );
};

export const combinationSlotModels = (
  combination: Record<string, unknown>
): SlotModels => ({
  outdoor: normalizeModel(combination.OutdoorModelNumber),
  indoor: normalizeModel(combination.IndoorModelNumber),
  blower: normalizeModel(combination.BlowerModelNumber)
});

export const systemSlotModels = (system: DuctedComparedSystem): SlotModels => {
  const condenser = system.configuration?.condensers?.[0];
  const zone = condenser?.zones?.[0];
  return {
    outdoor: normalizeModel(condenser?.selectedProduct?.model),
    indoor: normalizeModel(zone?.selectedProduct?.model),
    blower: normalizeModel(zone?.blowerProduct?.model)
  };
};

const slotModelsEqual = (left: SlotModels, right: SlotModels): boolean =>
  left.outdoor === right.outdoor &&
  left.indoor === right.indoor &&
  left.blower === right.blower;

const matchesCompareIdentity = (
  system: DuctedComparedSystem,
  lookup: DuctedCompareCardLookup
): boolean => {
  if (!isCompatibilitySystem(system)) return false;
  if (readCompatibilityPairs(system).length > 1) return false;
  if (readExistingSystemUniqueId(system) !== lookup.systemUniqueId) {
    return false;
  }
  if (hasKeptPairing(system) !== Boolean(lookup.keptPairing)) return false;
  const stampedId = system.compatibilityCombinationId ?? null;
  const lookupId = asCombinationId(lookup.combination.ID);
  if (stampedId != null && lookupId != null) {
    return stampedId === lookupId;
  }
  return slotModelsEqual(
    systemSlotModels(system),
    combinationSlotModels(lookup.combination)
  );
};

export const findComparedSystemForCard = <T extends DuctedComparedSystem>(
  systems: ReadonlyArray<T>,
  lookup: DuctedCompareCardLookup
): T | undefined => {
  const byCardId = systems.find(
    (system) => system.ductedCompareCardId === lookup.cardId
  );
  if (byCardId) return byCardId;
  return systems.find((system) => matchesCompareIdentity(system, lookup));
};
